// src/pages/courtowner/CourtOwnerBookings.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import CourtOwnerSidebar from "../../components/courtowner/CourtOwnerSidebar";
import { courtOwnerApi } from "../../api/courtOwnerApi";
import { FaCalendarAlt, FaListUl } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

/* ===== Config ===== */
const API_BASE = process.env.REACT_APP_API_BASE || "https://api.getfitindia.in/api";

/* ===== Helpers ===== */
const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
};
const parseMinutes = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
};
const normalizeName = (s) => (s || "").toLowerCase().replace(/\s+/g, "");
const toNum = (v) => (v == null || v === "" ? 0 : Number(v));
const inr = (v) => `₹ ${toNum(v).toLocaleString("en-IN")}`;

// ✅ Local date helper (fixes 1-day shift)
const formatDateYMD = (d) => {
  if (!(d instanceof Date)) d = new Date(d);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getUserSlotPrice = (b) => {
  const direct =
    b.amountPaidByUser ??
    b.userAmount ??
    b.userPaid ??
    b.customerPrice ??
    b.playerPrice ??
    b.slotPrice ??
    b.pricePerSlot ??
    b.price ??
    b.amount ??
    b.rate;
  if (toNum(direct)) return toNum(direct);

  const pay =
    b.payment?.userPaid ??
    b.payment?.amount ??
    b.paymentAmount ??
    (Array.isArray(b.payments)
      ? (b.payments.find((p) => p.status === "captured" || p.status === "paid")
          ?.amount) ?? b.payments[0]?.amount
      : 0);
  if (toNum(pay)) return toNum(pay);

  const items =
    (Array.isArray(b.items) && b.items) ||
    (Array.isArray(b.lineItems) && b.lineItems) ||
    (Array.isArray(b.orderItems) && b.orderItems) ||
    [];
  if (items.length) {
    const slotItem =
      items.find((i) =>
        String(i.name || i.title || "").toLowerCase().includes("slot")
      ) || items[0];
    const itemAmt =
      slotItem?.net ??
      slotItem?.subtotal ??
      slotItem?.amount ??
      slotItem?.price ??
      slotItem?.total;
    if (toNum(itemAmt)) return toNum(itemAmt);
  }
  return 0;
};
const getAmountPaid = (b) => {
  const explicit =
    b.amountPaid ??
    b.paidAmount ??
    b.paid ??
    b.payment?.amountCaptured ??
    b.payment?.captured ??
    b.payment?.amount ??
    (Array.isArray(b.payments)
      ? b.payments
          .filter((p) => p.status === "captured" || p.status === "paid")
          .reduce((sum, p) => sum + toNum(p.amount), 0)
      : 0);
  return toNum(explicit) ? toNum(explicit) : getUserSlotPrice(b);
};
const getTotal = (b) =>
  toNum(
    b.total ??
      b.grandTotal ??
      b.pricing?.total ??
      (getAmountPaid(b) + toNum(b.balance))
  );
const getBookerNameSoft = (b) =>
  b?.userId?.name ||
  b?.userName ||
  b?.playerName ||
  b?.customerName ||
  b?.user?.name ||
  b?.userDetails?.name ||
  b?.name;

async function fetchUserByIdFallback(id) {
  if (!id) return null;
  try {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(`${API_BASE}/users/${id}`, { headers });
    if (!res.ok) return null;
    const data = await res.json();
    const u = data?.user || data;
    if (!u) return null;
    return {
      _id: u._id || u.id,
      name: u.name || u.fullName || u.username || "User",
      mobile: u.mobile || u.phone || "",
      phone: u.phone || u.mobile || "",
    };
  } catch {
    return null;
  }
}
const getAuthHeader = () => {
  const token =
    localStorage.getItem("courtOwnerToken") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const isBlocked = (b) =>
  b?._isBlocked === true ||
  b?.status === "blocked" ||
  b?.type === "blocked" ||
  b?.isBlocked === true;

/* ===== Calendar trigger button ===== */
const CalendarTrigger = React.forwardRef(({ onClick }, ref) => (
  <button
    type="button"
    className="date-trigger"
    onClick={onClick}
    ref={ref}
    aria-label="Open calendar"
  >
    <FaCalendarAlt />
  </button>
));

export default function CourtOwnerBookings() {
  const [bookings, setBookings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [courts, setCourts] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'grid'
  const [calFilter, setCalFilter] = useState("all"); // 'all' | 'booked' | 'blocked'
  const [blockedSlots, setBlockedSlots] = useState([]); // ⬅ NEW

  const slotHeight = 62;
  const dayStartMin = 0;
  const timeSlots = Array.from({ length: 24 }, (_, i) => i);

  const dpRef = useRef(null);
  const monthStripRef = useRef(null);
  const activeChipRef = useRef(null);

  /* ===== Load venues ===== */
 /* ===== Load venues ===== */
useEffect(() => {
  (async () => {
    try {
      const v = await courtOwnerApi.getVenues();

      console.log("RAW VENUES RESPONSE →", v);

      let venueList = [];

      if (Array.isArray(v)) {
        // Backend directly returned array
        venueList = v;
      } else if (v && Array.isArray(v.venues)) {
        // Backend returned: { venues: [...] }
        venueList = v.venues;
      } else if (v && Array.isArray(v.data)) {
        // Some APIs return: { data: [...] }
        venueList = v.data;
      } else if (v && Array.isArray(v.results)) {
        // Or: { results: [...] }
        venueList = v.results;
      } else {
        console.error("❌ Invalid venues response:", v);
      }

      setVenues(venueList);
    } catch (err) {
      console.error("❌ Failed to load venues", err);
      setVenues([]); // fallback
    }
  })();
}, []);

  /* ===== Load courts + bookings for venue ===== */
  useEffect(() => {
    (async () => {
      if (!selectedVenue) return;
      setLoading(true);
      try {
        const courtRes = await courtOwnerApi.getAllCourts(selectedVenue);
        const courtList = Array.isArray(courtRes)
          ? courtRes
          : courtRes.courts || [];
        const normalized = courtList.map((c) => ({
          _id: c._id,
          courtName: c.courtName || c.name || "Court",
        }));
        setCourts(normalized);

        const all = await Promise.all(
          normalized.map((c) =>
            courtOwnerApi.getCourtBookings(selectedVenue, c._id)
          )
        );
        const combined = all.flatMap((r) =>
          Array.isArray(r) ? r : r.bookings || []
        );

        const userIds = new Set();
        combined.forEach((b) => {
          const uid =
            b.userId?._id || b.userId || b.user?._id || b.user?.id || null;
          if (uid) userIds.add(String(uid));
        });

        const userMap = new Map();
        await Promise.all(
          Array.from(userIds).map(async (id) => {
            const ready = combined.find((bk) => {
              const bid =
                bk.userId?._id ||
                bk.userId ||
                bk.user?._id ||
                bk.user?.id;
              return (
                String(bid || "") === id &&
                (bk.userId?.name || bk.user?.name)
              );
            });
            if (ready) {
              userMap.set(id, {
                name:
                  ready.userId?.name ||
                  ready.user?.name ||
                  ready.userName ||
                  ready.playerName ||
                  ready.customerName ||
                  "User",
                phone:
                  ready.userId?.mobile ||
                  ready.userId?.phone ||
                  ready.user?.mobile ||
                  ready.user?.phone ||
                  "",
              });
              return;
            }
            const u = await fetchUserByIdFallback(id);
            if (u)
              userMap.set(id, {
                name: u.name,
                phone: u.phone || u.mobile || "",
              });
          })
        );

        const enriched = combined.map((b) => {
          const inferredId =
            b.userId?._id || b.userId || b.user?._id || b.user?.id || null;
          const softName = getBookerNameSoft(b);
          const mapped = inferredId ? userMap.get(String(inferredId)) : null;
          return {
            ...b,
            _resolvedName: mapped?.name || softName || "User",
            _resolvedPhone:
              mapped?.phone ||
              b.userId?.mobile ||
              b.userId?.phone ||
              b.user?.mobile ||
              b.user?.phone ||
              "",
          };
        });

        setBookings(enriched);
      } catch {
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedVenue]);

  /* ===== Load BLOCKED slots only for calendar view ===== */
  useEffect(() => {
    if (!selectedVenue || !courts.length || viewMode !== "grid") {
      setBlockedSlots([]);
      return;
    }

    // ✅ Use local Y-M-D string instead of toISOString()
    const dateStr = formatDateYMD(selectedDate);

    (async () => {
      try {
        const headers = getAuthHeader();
        const results = await Promise.all(
          courts.map(async (c) => {
            try {
              const res = await fetch(
                `${API_BASE}/courtowner/blockslots/${c._id}?date=${dateStr}`,
                { headers }
              );
              if (!res.ok) return [];
              const data = await res.json();
              if (!Array.isArray(data)) return [];
              // mark each as blocked slot; keep server's date
              return data.map((s) => ({
                ...s,
                _isBlocked: true,
              }));
            } catch {
              return [];
            }
          })
        );
        setBlockedSlots(results.flat());
      } catch (e) {
        console.error("Failed to load blocked slots", e);
      }
    })();
  }, [selectedVenue, courts, selectedDate, viewMode]);

  /* ===== Filter bookings by selected date (for LIST view) ===== */
  const filteredBookings = useMemo(() => {
    return (bookings || []).filter((b) => {
      const d = new Date(b.date);
      return (
        d.getFullYear() === selectedDate.getFullYear() &&
        d.getMonth() === selectedDate.getMonth() &&
        d.getDate() === selectedDate.getDate()
      );
    });
  }, [bookings, selectedDate]);

  /* ===== Unique bookings (LIST view only) ===== */
  const uniqueBookings = useMemo(() => {
    const map = new Map();
    filteredBookings.forEach((b) => {
      const cid =
        b.courtId?._id || b.courtId || normalizeName(b.courtName);
      const key = [
        cid,
        b.date?.slice(0, 10),
        b.startTime,
        b.endTime,
        isBlocked(b),
      ].join("|");
      if (!map.has(key)) map.set(key, b);
    });
    return Array.from(map.values());
  }, [filteredBookings]);

  /* ===== Calendar items: BOOKINGS + BLOCKED (for GRID view) ===== */
  const calendarItems = useMemo(() => {
    // ✅ Also use local Y-M-D here
    const dateStr = formatDateYMD(selectedDate);

    const blockedForDay = blockedSlots.filter(
      (s) => (s.date || "").slice(0, 10) === dateStr
    );

    return [
      ...uniqueBookings, // normal bookings
      ...blockedForDay, // already marked with _isBlocked
    ];
  }, [uniqueBookings, blockedSlots, selectedDate]);

  const monthDays = useMemo(() => {
    const y = selectedDate.getFullYear();
    const m = selectedDate.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const days = [];
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  }, [selectedDate]);

  const selectedVenueName = useMemo(() => {
    const v = venues.find((x) => x._id === selectedVenue);
    return v?.name || "";
  }, [venues, selectedVenue]);

  useEffect(() => {
    const container = monthStripRef.current;
    const chip = activeChipRef.current;
    if (!container || !chip) return;
    const targetLeft =
      chip.offsetLeft -
      (container.clientWidth / 2 - chip.clientWidth / 2);
    container.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: "smooth",
    });
  }, [selectedDate, monthDays.length]);

  const handleUnblock = async (slot) => {
    if (!slot?._id) return;
    const ok = window.confirm(
      `Unblock this slot?\n${formatTime(
        slot.startTime
      )} – ${formatTime(slot.endTime)}${
        slot?.reason ? `\nReason: ${slot.reason}` : ""
      }`
    );
    if (!ok) return;
    try {
      const res = await fetch(
        `${API_BASE}/courtowner/blockslots/${slot._id}`,
        {
          method: "DELETE",
          headers: { ...getAuthHeader() },
        }
      );
      if (!res.ok) throw new Error("Failed to unblock slot");
      // remove from blocked slots & bookings (if any)
      setBlockedSlots((prev) =>
        prev.filter((b) => b._id !== slot._id)
      );
      setBookings((prev) =>
        prev.filter((b) => b._id !== slot._id)
      );
    } catch (err) {
      alert(err.message);
    }
  };

  return (
  <>
    <style>{`
        :root{
          --navy:#0C295F; --line:#E6EBF2; --muted:#8A94A6; --orange:#FF5A00; --soft:#EAEFF5;
          --booked-bg:#E8F7EF; --booked-border:#B6E3C9; --booked-text:#0C295F;
          --blocked-bg:#3B4A6B; --blocked-border:#2B3650; --blocked-text:#FFFFFF;
        }

        /* removed old .layout and main layout as we now use Tailwind layout */

        .top-row{ display:grid; grid-template-columns: 1fr auto auto; align-items:center; gap:12px; padding:0 0 10px 0; }
        .page-title{ color:var(--navy); font-weight:800; font-size:22px; }
        .pill{ display:inline-flex; align-items:center; gap:10px; background:#fff; border:1px solid var(--line); border-radius:12px; padding:8px 12px; font-weight:700; color:var(--navy); box-shadow:0 1px 3px rgba(0,0,0,.05); cursor:pointer; white-space:nowrap; }

        /* ==== DATE ROW + SELECT ==== */
        .date-row{
          display:grid;
          grid-template-columns: auto 1fr auto;  /* icon | date text | ground select */
          align-items:center;
          gap:10px;
          color:var(--navy);
          margin:0 0 10px 0;
        }
        .date-trigger{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          width:36px;
          height:36px;
          border-radius:10px;
          border:1px solid var(--line);
          background:#fff;
          color:#0C295F;
          box-shadow:0 1px 2px rgba(0,0,0,.05);
          flex-shrink:0;
        }
        .date-text{
          font-weight:800;
          letter-spacing:.2px;
          cursor:pointer;
          user-select:none;
          font-size:13px;
        }
        .select{
          background:#fff;
          border:1px solid var(--line);
          border-radius:8px;
          padding:8px 10px;
          font-size:14px;
          color:#374151;
          box-shadow:0 1px 2px rgba(0,0,0,.04);
          max-width: 220px;   /* desktop / tablet */
        }

        /* 🔻 MOBILE FIX: stack ground select below & full width */
        @media (max-width: 600px){
          .date-row{
            grid-template-columns: auto 1fr;  /* only icon + date text on first row */
            grid-auto-rows: auto;
          }
          .select{
            grid-column: 1 / -1;  /* span both columns on its own row */
            width: 100%;
            max-width: none;
            margin-top: 4px;
          }
        }

        .week-strip{ display:flex; gap:4px; overflow-x:auto; padding:6px 0 12px; margin-bottom:6px; scrollbar-width:none; scroll-behavior:smooth; }
        .week-strip::-webkit-scrollbar{ display:none; }
        .chip{ min-width:56px; text-align:center; border:0; background:transparent; cursor:pointer; }
        .dow{ font-size:14px; font-weight:700; color:var(--muted); }
        .badge{ margin-top:8px; width:42px; height:42px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center; font-weight:800; color:#0C295F; background:var(--soft); }
        .chip.active .dow{ color:var(--orange); }
        .chip.active .badge{ background:var(--orange); color:#fff; }

        .cal-card{ background:#fff; border:1px solid var(--line); border-radius:14px; overflow:hidden; }
        .time-col{ width:64px; border-right:1px solid var(--line); flex-shrink:0; }
        .time-head{ background:#0C295F; color:#fff; height:65px; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; border-bottom:1px solid #1f2e52; }
        .time-cell{ height:${slotHeight}px; display:flex; align-items:flex-start; justify-content:center; padding-top:8px; font-size:12px; color:#0C295F; border-top:1px solid #f1f5f9; }

        /* 🔽 courts header row uses full remaining width */
        .courts-head{
          display:flex;
          width:100%;
          border-bottom:1px solid var(--line);
        }

        /* 🔽 columns container also fills remaining width */
        .cols{
          position:relative;
          display:flex;
          width:100%;
        }

        /* 🔽 each court column flexes to fill space, but has min width for scroll */
        .court-h{
          background:#0C295F;
          color:#fff;
          text-align:center;
          padding:12px 0;
          border-left:1px solid #1f2e52;
          font-weight:600;
          flex:1 0 140px;
          min-width:140px;
        }
        .court-h small{ display:block; font-size:11px; opacity:.9; }

        .col{
          position:relative;
          border-left:1px solid var(--line);
          height:${slotHeight * 24}px;
          flex:1 0 140px;
          min-width:140px;
        }

        .grid-line{ position:absolute; left:0; right:0; border-top:1px solid #f1f5f9; }

        .slot{ position:absolute; left:4px; right:4px; border-radius:8px; padding:6px; font-weight:700; font-size:11px; box-shadow:0 1px 2px rgba(0,0,0,.06); display:flex; flex-direction:column; justify-content:center; }
        .slot.booked{ background:#007d2e; border:1px solid var(--booked-border); color:white; }
        .slot.blocked{ background:#f97316; color:white; cursor:pointer; }
        .slot .sub{ font-size:10px; opacity:.9; margin-top:2px; }

        .book-card{ background:#fff; border:1px solid var(--line); border-radius:16px; padding:16px 53px; box-shadow:0 4px 12px rgba(12,41,95,.06); }
        .book-head{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:12px; }
        .badges{ display:flex; gap:10px; flex-wrap:wrap; }
        .badgePill{ display:inline-flex; align-items:center; justify-content:center; height:28px; padding:0 12px; border-radius:999px; font-size:12px; font-weight:800; letter-spacing:.2px; line-height:1; }
        .ok{ background:#16A34A; color:#fff; }
        .outline{ background:#fff; color:#334155; border:1.5px solid #94A3B8; }
        .book-id{ font-size:12px; color:#6B7280; font-weight:700; white-space:nowrap; }

        .dateRow{ display:flex; justify-content:space-between; color:#0C295F; font-weight:800; font-size:13px; }
        .timeRow{ color:#0C295F; font-weight:800; font-size:12px; margin-top:1px; }
        .venueRow{ display:grid; grid-template-columns: 1fr auto; align-items:center; gap:12px; margin-top:8px; }
        .venue{ color:#0C295F; font-weight:800; font-size:14px; }
        .court{ color:#6B7280; font-size:13px; }
        .hr{ height:1px; background:#EDF2F7; margin:12px 0; }
        .meta3{ display:grid; grid-template-columns:1fr 1fr; gap:12px; color:#0C295F; font-weight:800; font-size:14px; }
        .label{ display:block; color:#6B7280; font-weight:700; margin-bottom:4px; font-size:12px; }
        .value{ font-weight:800; }
        .book-foot{ display:flex; align-items:center; justify-content:space-between; margin-top:10px; }
        .call-btn{ display:inline-flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:999px; background:#0C295F; color:#fff; box-shadow:0 2px 6px rgba(12,41,95,.2); }
        .space-y-4 > * + * { margin-top:14px; }
      `}</style>

    {/* OUTER LAYOUT LIKE DIRECTORY / MY VENUES */}
    <div className="min-h-screen bg-[#f7f9fc]">
      {/* Fixed sidebar (desktop) */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white shadow z-30">
        <CourtOwnerSidebar />
      </aside>

      {/* Main area */}
      <main className="lg:pl-64">
        {/* Header bar */}
        <div className="bg-[#0C295F] text-white sticky top-0 z-20 py-2">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
            {/* Mobile sidebar */}
            <div className="lg:hidden">
              <CourtOwnerSidebar />
            </div>

            <h1 className="text-lg font-semibold sm:ms-0  lg:ms-0">
              My Bookings
            </h1>
          </div>
        </div>

        {/* Page content */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Top row: title + view toggle (unchanged) */}
          <div className="top-row">
            <h1 className="page-title">Booking Overview</h1>
            <button
              type="button"
              className="pill"
              onClick={() =>
                setViewMode((p) => (p === "grid" ? "list" : "grid"))
              }
            >
              <FaListUl />{" "}
              {viewMode === "grid" ? "List View" : "Calendar View"}
            </button>
          </div>

          {/* Date & Venue */}
          <div className="date-row">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              customInput={<CalendarTrigger />}
              ref={dpRef}
              popperPlacement="bottom-start"
              showPopperArrow={false}
              wrapperClassName="dp-wrapper"
            />
            <div
              className="date-text"
              onClick={() => dpRef.current?.setOpen(true)}
              title="Change date"
              style={{ marginTop: 1 }}
            >
              {selectedDate.toLocaleDateString("en-GB", {
                day: "2-digit",
              })}{" "}
              {selectedDate.toLocaleDateString("en-GB", {
                month: "short",
              })}
              ,{" "}
              {selectedDate.toLocaleDateString("en-GB", {
                weekday: "long",
              })}
            </div>

            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="select"
            >
              <option value="">Select Ground</option>
              {Array.isArray(venues) &&
  venues.map((v) => (

                <option key={v._id} value={v._id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          {/* Month strip */}
          <div className="week-strip" ref={monthStripRef}>
            {monthDays.map((d, i) => {
              const isActive =
                d.toDateString() === selectedDate.toDateString();
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedDate(d)}
                  className={`chip ${isActive ? "active" : ""}`}
                  ref={isActive ? activeChipRef : null}
                >
                  <div className="dow">
                    {d.toLocaleDateString("en-US", {
                      weekday: "short",
                    })}
                  </div>
                  <div className="badge">{d.getDate()}</div>
                </button>
              );
            })}
          </div>

          {/* ===== LIST VIEW ===== */}
          {viewMode === "list" && selectedVenue && !loading && (
            <div className="space-y-4 pb-8">
              {uniqueBookings.length === 0 ? (
                <p
                  style={{
                    textAlign: "center",
                    color: "#6b7280",
                  }}
                >
                  No bookings for selected date.
                </p>
              ) : (
                uniqueBookings.map((b) => {
                  const payer =
                    b._resolvedName?.trim() ||
                    getBookerNameSoft(b)?.trim() ||
                    b.user?.name ||
                    b.userId?.name ||
                    b.user?.fullName ||
                    b.userId?.fullName ||
                    b.playerName ||
                    b.customerName ||
                    "Unknown User";

                  const idText =
                    b.bookingId || b._id?.slice(-8)?.toUpperCase();
                  const venueText =
                    b.venueName || selectedVenueName || "—";
                  const courtText = `Court ${b.courtName || "N/A"}`;
                  const paid = getAmountPaid(b);
                  const total = getTotal(b);

                  const phone =
                    b._resolvedPhone ||
                    b.phone ||
                    b.user?.phone ||
                    b.userId?.mobile ||
                    b.userId?.phone ||
                    "";

                  return (
                    <div key={b._id || idText} className="book-card">
                      <div className="book-head">
                        <div className="badges">
                          <span className="badgePill ok">
                            {(b.status || "CONFIRMED").toUpperCase()}
                          </span>
                          <span className="badgePill outline">
                            {b.razorpay_payment_id ? "ONLINE" : "OFFLINE"}
                          </span>
                        </div>
                        <div className="book-id">ID: {idText}</div>
                      </div>

                      <div className="dateRow">
                        <div>
                          {new Date(b.date).toLocaleDateString("en-GB", {
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                        <div className="timeRow">
                          {formatTime(b.startTime)} – {formatTime(b.endTime)}
                        </div>
                      </div>

                      <div className="venueRow">
                        <div className="venue">{venueText}</div>
                        <div className="court">{courtText}</div>
                      </div>

                      <div className="hr" />

                      <div className="meta3">
                        <div>
                          <span className="label">Amount Paid</span>
                          <div className="value">{inr(paid)}</div>
                        </div>
                        <div>
                          <span className="label">Total</span>
                          <div
                            className="value"
                            style={{ color: "#FF5A00" }}
                          >
                            {inr(total)}
                          </div>
                        </div>
                      </div>

                      <div className="hr" />

                      <div className="book-foot">
                        {/* Left: payer name + mobile */}
                        <div>
                          <div
                            style={{
                              fontWeight: 800,
                              color: "var(--navy)",
                              fontSize: 14,
                            }}
                          >
                            {payer}
                          </div>

                          {phone && (
                            <div
                              style={{
                                fontSize: 12,
                                color: "#555",
                                marginTop: 2,
                              }}
                            >
                              {phone}
                            </div>
                          )}
                        </div>

                        {/* Right: call button */}
                        {phone ? (
                          <a
                            className="call-btn"
                            href={`tel:${phone}`}
                            title="Call"
                          >
                            📞
                          </a>
                        ) : (
                          <span />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ===== CALENDAR VIEW ===== */}
          {viewMode === "grid" && selectedVenue && !loading && (
            <>
              {/* Filter pills */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                {["all", "booked", "blocked"].map((f) => (
                  <button
                    key={f}
                    className="pill"
                    style={{
                      padding: "6px 10px",
                      borderColor:
                        calFilter === f ? "var(--orange)" : "var(--line)",
                      color:
                        calFilter === f ? "var(--orange)" : "var(--navy)",
                    }}
                    onClick={() => setCalFilter(f)}
                  >
                    {f === "all"
                      ? "All"
                      : f[0].toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>

              <div className="cal-card" style={{ marginTop: 6 }}>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  {/* Time Column */}
                  <div className="time-col">
                    <div className="time-head">Time</div>
                    {timeSlots.map((hour) => (
                      <div key={hour} className="time-cell">
                        {(hour % 12 || 12).toString().padStart(2, "0")}:00{" "}
                        {hour >= 12 ? "PM" : "AM"}
                      </div>
                    ))}
                  </div>

                  {/* Courts */}
                  <div
                    style={{
                      overflowX: "auto",
                      width: "100%",
                    }}
                  >
                    <div className="courts-head">
                      {courts.length > 0 ? (
                        courts.map((c) => (
                          <div
                            key={c._id}
                            className="court-h"
                            title={`${c.courtName} – Full Court`}
                          >
                            {c.courtName}
                            <small>Full Court</small>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: "10px 0",
                            textAlign: "center",
                            width: "100%",
                            color: "#6b7280",
                          }}
                        >
                          No Courts Found
                        </div>
                      )}
                    </div>

                    {/* Columns + slots */}
                    <div className="cols">
                      {courts.map((c) => (
                        <div key={c._id} className="col">
                          {timeSlots.map((hour) => (
                            <div
                              key={`line-${c._id}-${hour}`}
                              className="grid-line"
                              style={{
                                top: `${hour * slotHeight}px`,
                              }}
                            />
                          ))}

                          {calendarItems
                            .filter((b) => {
                              const sameCourt =
                                (b.courtId?._id || b.courtId) === c._id ||
                                normalizeName(b.courtName) ===
                                  normalizeName(c.courtName);
                              if (!sameCourt) return false;
                              const blocked = isBlocked(b);
                              if (calFilter === "blocked") return blocked;
                              if (calFilter === "booked") return !blocked;
                              return true;
                            })
                            .map((b) => {
                              const startMin = parseMinutes(b.startTime);
                              const endMin = parseMinutes(b.endTime);
                              const durationHrs = Math.max(
                                (endMin - startMin) / 60,
                                0.25
                              );
                              const topPx =
                                ((startMin - dayStartMin) / 60) * slotHeight;
                              const blocked = isBlocked(b);

                          const title = blocked
  ? "Bulk slots blocked by Owner"   // keep as it is
  : (b._resolvedName ||
     getBookerNameSoft(b) ||
     b.user?.name ||
     b.userId?.name ||
     "User");   // ONLY username

const subtitle = blocked
  ? (b.reason || "Coaching & Membership")
  : 
  // `${formatTime(b.startTime)} – ${formatTime(b.endTime)}`  ; 

`Booked by ${ b._resolvedName ||getBookerNameSoft(b) ||"User"}`;

                              return (
                                <div
                                  key={`${b._id}-${b.startTime}-${b.endTime}-${
                                    blocked ? "blk" : "bkd"
                                  }`}
                                  className={`slot ${
                                    blocked ? "blocked" : "booked"
                                  }`}
                                  style={{
                                    top: `${topPx}px`,
                                    height: `${slotHeight * durationHrs - 4}px`,
                                  }}
                                  title={
                                    blocked
                                      ? `Blocked: ${
                                          b.reason || "—"
                                        }\n${formatTime(
                                          b.startTime
                                        )} – ${formatTime(
                                          b.endTime
                                        )}\nClick to Unblock`
                                      : `Booked by ${
                                          b._resolvedName ||
                                          getBookerNameSoft(b) ||
                                          "User"
                                        }\n${formatTime(
                                          b.startTime
                                        )} – ${formatTime(b.endTime)}`
                                  }
                                  onClick={
                                    blocked ? () => handleUnblock(b) : undefined
                                  }
                                >
                                  <div className="truncate">{title}</div>
                                  <div className="sub">{subtitle}</div>
                                </div>
                              );
                            })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {!selectedVenue && (
            <div
              style={{
                textAlign: "center",
                color: "var(--navy)",
                marginTop: 24,
                fontSize: 14,
              }}
            >
              Please select a ground to view bookings.
            </div>
          )}
        </div>
      </main>
    </div>
  </>
);

}